/*
 *  Copyright (c) 2021.
 *  This file is part of the Compo B2B application.
 *  (c) Compo soft - Sergey Sukhenko <cyxenko@gmail.com>
 *  For the full copyright and license information, please view the LICENSE
 *  file that was distributed with this source code.
 */

import {HttpParams} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {FormControl} from '@angular/forms';
import {ParamMap} from '@angular/router';
import {FormlyFieldConfig} from '@ngx-formly/core';
import {TuiDay, TuiDayRange} from '@taiga-ui/cdk';
import {Environment, FeatureFilter} from 'compob2b-libs';
import {endOfToday, startOfMonth} from 'date-fns';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {
  ProductFilterService as ProductFilterServiceCustomer
} from '../../../../../compob2b-customer/src/app/cabinet/product/services/product-filter.service';
import {translations} from '../../../environments/translations.ru';

@Injectable({
  providedIn: 'root'
})
/**
 * The service prepares configuration for ngx-formly
 * Configuration is CompoColumns type in each module's config constant
 */
export class ProductFilterService extends ProductFilterServiceCustomer {
  yesNoSelectFilterKeys: string[] = ['enabled', 'deleted'];
  environment = inject(Environment);

  getYesNoFilterKeys(): string[] {
    return this.yesNoSelectFilterKeys;
  }

  getProductFilter(uri, id, params: ParamMap): Observable<any> {

    let httpParams = new HttpParams();

    params.keys.forEach(key => httpParams = httpParams.set(key, params.getAll(key).join(',')));

    return this.compoApiService.getWithParams<FeatureFilter>(uri + '/get-filter-fields/' + id, httpParams).pipe(
      map(response => (
        {
          fieldsFeatures: this.configFields(response, params),
          templateAggr: response.data.templateAggr
        }
      ))
    );
  }

  configFields(response, params) {
    const textFields = ['header', 'barCode', 'articul', 'articuls', 'syncUid'];

    const selectFields = [
      'productStatusId',
      'brandId',
      'manufacturerId',
      'manufacturerSeriesId',
      'productGroupId',
      'supplierId',
      'classId',
      'tag',
      'systemTag',
      'featureUnionCondition',
      'countryId'
    ];

    const nullableSelectFields = ['brandId', 'countryId'];
    if (this.environment.useProductTemplateFeatures) {
      selectFields.splice(2, 0, 'templateId');
    }

    this.fieldsFeatures = [];
    if (response.data.featureProduct.length) {
      response.data.featureProduct.forEach(element => {
        if (element.type === 'input') {
          let defaultValue = '';

          textFields
            .filter(field => element.code === field && params.get(field) !== null)
            .forEach(field => defaultValue = params.get(field));

          const fieldConfig: FormlyFieldConfig = {
            key: element.code,
            type: 'input-filter-property',
            defaultValue,
            templateOptions: {
              label: element.header,
              placeholder: element.header
            }
          };
          this.fieldsFeatures.push(fieldConfig);
        }

        if (element.type === 'textarea') {
          let defaultValue = '';

          textFields
            .filter(field => element.code === field && params.get(field) !== null)
            .forEach(field => defaultValue = params.get(field));

          const fieldConfig: FormlyFieldConfig = {
            key: element.code,
            type: 'textarea',
            defaultValue,
            templateOptions: {
              label: element.header,
              placeholder: element.header
            }
          };
          this.fieldsFeatures.push(fieldConfig);
        }

        if (element.type === 'checkbox' && !this.getYesNoFilterKeys().includes(element.code)) {
          const defaultValue = '';
          const fieldConfig: FormlyFieldConfig = {
            key: element.code,
            type: 'checkbox',
            defaultValue,
            templateOptions: {
              label: element.header,
              placeholder: element.header
            }
          };
          this.fieldsFeatures.push(fieldConfig);
        }

        if (this.getYesNoFilterKeys().includes(element.code)) {
          const optionsList: any = [
            {value: 'true', label: translations.yes}, {value: 'false', label: translations.no}
          ];
          let defaultValue = 0;

          if (params.get(element.code) !== null && params.get(element.code) !== '0') {
            defaultValue = params.get(element.code);
          }

          const fieldConfig: FormlyFieldConfig = {
            key: element.code,
            type: 'select-filter-property',
            defaultValue,
            templateOptions: {
              label: element.header,
              placeholder: translations.placeholderSelect,
              options: optionsList
            }
          };
          this.fieldsFeatures.push(fieldConfig);
        }

        if (element.type === 'select') {
          let optionsList = [];
          if (element.featureValue !== null) {
            optionsList = element.featureValue.map(value => {
              return {
                value: value.id === null ? 'null' : value.id,
                label: value.value
              };
            });
          }

          if (nullableSelectFields.includes(element.code)) {
            optionsList.unshift({value: 'null', label: translations.notFilled});
          }

          let defaultValue = null;
          selectFields
            .filter(field => element.code === field && params.get(field) !== null)
            .forEach(field => {
              defaultValue = params.get(field).split(',').map(itemValue => itemValue === 'null' ? 'null' : Number(itemValue));
            });

          const fieldConfig: FormlyFieldConfig = {
            key: element.code,
            type: 'select-multi-filter-property',
            defaultValue,
            templateOptions: {
              multipleValue: true,
              label: element.header,
              placeholder: translations.placeholderSelect,
              options: optionsList
            }
          };
          this.fieldsFeatures.push(fieldConfig);
        }

        if (element.type === 'dateRange') {
          let defaultValue = null;
          if (params.get(element.code + '_from') !== null && params.get(element.code + '_to') !== null) {
            defaultValue = new TuiDayRange(
              TuiDay.normalizeParse(params.get(element.code + '_from'), 'YMD'),
              TuiDay.normalizeParse(params.get(element.code + '_to'), 'YMD'));
          }
          const startMonth = startOfMonth(new Date());
          const today = endOfToday();

          const fieldConfig: FormlyFieldConfig = {
            formControl: new FormControl(
              new TuiDayRange(new TuiDay(startMonth.getFullYear(), startMonth.getMonth(), startMonth.getDate()),
                new TuiDay(today.getFullYear(), today.getMonth(), today.getDate()))),
            defaultValue,
            key: element.code,
            type: 'date-range-picker',
            templateOptions: {
              label: element.header,
              placeholder: element.header
            }
          };

          this.fieldsFeatures.push(fieldConfig);
        }
      });
    }

    if (response.data.features !== null && response.data.features.length) {
      response.data.features.forEach(item => {

        const optionsList = item.values.map(value => {
          return {value: value.id, label: value.value, count: response.data.featureValueCount[value.id]};
        });
        let defaultValue = [];
        if (params.get(item.id.toString()) !== null) {
          const values = params.get(item.id.toString()).split(',');
          defaultValue = values.map(w => parseInt(w, 10));
        }

        /**
         * checkbox
         */

        if (item.featureTypeId === 5) {
          let defaultValue1 = false;
          if (params.get(item.id.toString()) !== null && params.get(item.id.toString()) !== '0') {
            defaultValue1 = JSON.parse(params.get(item.id.toString()));
          }

          const fieldConfig: FormlyFieldConfig = {
            key: item.id,
            type: 'checkbox',
            defaultValue: defaultValue1,
            templateOptions: {
              label: item.header,
              placeholder: item.header,
              options: optionsList
            }
          };
          this.fieldsFeatures.push(fieldConfig);
        }

        /**
         * interval
         */
        if (item.featureTypeId === 7 || item.featureTypeId === 4) {

          let defaultValueStart = '';
          let defaultValueEnd = '';
          const values1 = params.get(item.id.toString());
          if (values1 !== undefined && values1 !== null) {
            const valuesList = values1.split(';');
            defaultValueStart = valuesList[0];
            defaultValueEnd = valuesList[1];

          }
          if (response.data.minMax[item.id.toString()] !== undefined) {
            const fieldConfig: FormlyFieldConfig = {
              key: item.id,
              wrappers: ['interval-wrapper'],
              templateOptions: {label: item.header},
              fieldGroup: [{
                key: 'start',
                type: 'input',
                modelOptions: {
                  updateOn: 'blur',
                },
                defaultValue: defaultValueStart,
                templateOptions: {
                  type: 'number',
                  min: response.data.minMax[item.id].min,
                  max: response.data.minMax[item.id].max,
                  placeholder: 'от ' + response.data.minMax[item.id].min
                }
              },
                {
                  key: 'stop',
                  type: 'input',
                  modelOptions: {
                    updateOn: 'blur',
                  },
                  defaultValue: defaultValueEnd,
                  templateOptions: {
                    type: 'number',
                    min: response.data.minMax[item.id].min,
                    max: response.data.minMax[item.id].max,
                    placeholder: 'до ' + response.data.minMax[item.id].max
                  }
                }],
            };
            this.fieldsFeatures.push(fieldConfig);
          }
        }

        /**
         * select features
         */

        if (item.featureTypeId === 1) {

          const optionsList2 = item.values.map(value => {
            let cntInAggegation = 0;
            if (response.data.featureAndValuesAggr[item.id] !== undefined &&
              response.data.featureAndValuesAggr[item.id][value.id] !== undefined) {
              cntInAggegation = response.data.featureAndValuesAggr[item.id][value.id];
            }
            return {
              value: value.id, label: value.value + ' (' + cntInAggegation + ')'
            };
          });

          const defaultValue2 = [];
          if (params.get(item.id.toString()) !== null) {
            const values = params.get(item.id.toString()).split(',');

            optionsList2.forEach(item2 => {
                if (values.includes(item2.value.toString())) {
                  defaultValue2.push(item2);
                }
              }
            );
          }

          if (optionsList2.length > 1) {
            const fieldConfig: FormlyFieldConfig = {
              key: item.id,
              type: 'select-multi-filter-property',
              defaultValue,
              templateOptions: {
                multipleValue: true,
                label: item.header,
                placeholder: item.header,
                options: optionsList2
              }
            };
            this.fieldsFeatures.push(fieldConfig);
          }

        }

      });
    }
    return this.fieldsFeatures;
  }
}
